import React, { useState } from 'react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine, ResponsiveContainer,
} from 'recharts'
import { KpiCard } from '../components/KpiCard'
import { ChartCard } from '../components/ChartCard'
import { AnalyticsTable, RateCellRenderer, RedValueRenderer } from '../components/AnalyticsTable'
import { SubTabBar } from '../components/SubTabBar'
import {
  KpiRow,
  StatusBarWrapper,
  StatusBar,
  StatusSegment,
  StatusLegend,
  LegendItem,
  LegendDot,
  ChartCardWrapper,
  ChartCardTitle,
} from '../HrAnalytics.styled'
import {
  MOCK_ATTENDANCE_KPIS,
  MOCK_ATTENDANCE_TREND,
  MOCK_STATUS_DISTRIBUTION,
  MOCK_ATTENDANCE_BY_DEPT,
  MOCK_ATTENDANCE_BY_ROLE,
} from 'features/hrAnalytics/mockData/attendance.mock'

const KpiIcons: Record<string, React.ReactNode> = {
  person: <svg viewBox='0 0 24 24'><path d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z' /></svg>,
  checkCircle: <svg viewBox='0 0 24 24'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z' /></svg>,
  warning: <svg viewBox='0 0 24 24'><path d='M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z' /></svg>,
  chart: <svg viewBox='0 0 24 24'><path d='M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z' /></svg>,
}

const DEPT_COLUMNS = [
  { key: 'name', label: 'Department', sortable: true, width: '20%' },
  { key: 'expected', label: 'Expected', sortable: true, width: '20%' },
  { key: 'present', label: 'Present', sortable: true, width: '20%' },
  {
    key: 'absent',
    label: 'Absent',
    sortable: true,
    width: '15%',
    renderCell: (val: number) => <RedValueRenderer value={val} />,
  },
  {
    key: 'rate',
    label: 'Rate',
    sortable: true,
    width: '25%',
    renderCell: (val: number) => <RateCellRenderer value={val} />,
  },
]

export const AttendanceTab = () => {
  const [period, setPeriod] = useState('Day')
  const [tableTab, setTableTab] = useState('department')

  const tableData = tableTab === 'department' ? MOCK_ATTENDANCE_BY_DEPT : MOCK_ATTENDANCE_BY_ROLE

  return (
    <>
      <KpiRow>
        {MOCK_ATTENDANCE_KPIS.map((kpi, idx) => (
          <KpiCard
            key={idx}
            icon={KpiIcons[kpi.iconKey || '']}
            value={kpi.value}
            label={kpi.label}
            sublabel={kpi.sublabel}
            trend={kpi.trend}
            accentColor={kpi.accentColor}
          />
        ))}
      </KpiRow>

      <ChartCard
        title='Attendance Rate Trend'
        periods={['Day', 'Week', 'Month']}
        activePeriod={period}
        onPeriodChange={setPeriod}
        legend={[
          { label: 'Rate', color: '#ff9900' },
          { label: '85% Target', color: '#FF4F46', dashed: true },
        ]}
      >
        <ResponsiveContainer width='100%' height={300}>
          <LineChart data={MOCK_ATTENDANCE_TREND}>
            <CartesianGrid strokeDasharray='3 3' stroke='#46425520' />
            <XAxis dataKey='date' tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#B0B0B0' }} tickLine={false} />
            <Tooltip />
            <ReferenceLine y={85} stroke='#FF4F46' strokeDasharray='8 8' strokeWidth={2} />
            <Line
              type='monotone'
              dataKey='rate'
              stroke='#ff9900'
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#ff9900' }}
            />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCardWrapper>
        <ChartCardTitle>Status Distribution</ChartCardTitle>
        <StatusBarWrapper>
          <StatusBar>
            {MOCK_STATUS_DISTRIBUTION.map((status) => (
              <StatusSegment
                key={status.label}
                percent={status.percent}
                color={status.color}
              >
                {status.percent > 5 ? `${status.percent}%` : ''}
              </StatusSegment>
            ))}
          </StatusBar>
          <StatusLegend>
            {MOCK_STATUS_DISTRIBUTION.map((status) => (
              <LegendItem key={status.label}>
                <LegendDot color={status.color} />
                {status.label}
              </LegendItem>
            ))}
          </StatusLegend>
        </StatusBarWrapper>
      </ChartCardWrapper>

      <SubTabBar
        tabs={[
          { key: 'department', label: 'By Department' },
          { key: 'role', label: 'By Role' },
        ]}
        activeTab={tableTab}
        onTabChange={setTableTab}
      />

      <AnalyticsTable columns={DEPT_COLUMNS} data={tableData} />
    </>
  )
}
